import React, { useEffect, useRef, useState } from "react";
import bulbImage from "../../../images/buld.png";
import Swal from "sweetalert2";
import { z } from "zod";
import Footer from "../../../core/common/footer/Footer";
import useApi from "../../../core/customhook/useApi";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { apibaseurl } from "../../../environment";
import { all_routes } from "../../router/all_routes";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../auth/AuthContext";
import { disabledStatuses } from "../../../core/common/DateValidHelper";

type Option = string | { label: string; value: string };
type CareerGapField = {
  field_uuid: string;
  field_id: string;
  label: string;
  type: string;
  placeholder?: string;
  required?: boolean;
  display_order?: number;
  options?: Option[];
  value?: unknown;
  validation_rules?: {
    min_length?: number;
    max_length?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
  file_config?: { allowed_types?: string[]; max_size_mb?: number };
  uploaded_files?: { url: string; original_name?: string }[];
};

const CareerGap = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { authData } = useAuth();
  const candidateId = authData?.candidate?.candidateId;
  const templates = useSelector((s: RootState) => s.package.templates);

  // Keep the existing check_name lookup, exactly as requested.
  const aadharTemplate = templates.find(
    (item: any) => item.check_name === "Career Gap Comments",
  );

  const assignmentTemplateId = aadharTemplate?.assignment_template_id;
  const caseId = aadharTemplate?.case_id;
  const assignmentId = aadharTemplate?.assignment_id;
  const templateStatus = aadharTemplate?.status;

  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};

  const [formFields, setFormFields] = useState<CareerGapField[]>([]);
  const [formData, setFormData] = useState<
    Record<string, string | File | null>
  >({});
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}`,
    { method: "GET", autoCall: false, handleGlobalError: false },
  );
  const templateSaveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    { method: "POST", autoCall: false, handleGlobalError: false },
  );
  const templateUpdateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    { method: "PUT", autoCall: false, handleGlobalError: false },
  );
  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    { method: "POST", autoCall: false, handleGlobalError: false },
  );

  const fetchCareerGapForm = async () => {
    if (!assignmentTemplateId) return;
    try {
      const response = await formFieldsAPI.callApi();
      if (!response?.success) {
        Swal.fire(
          "Unable to load",
          response?.message || "Please try again.",
          "error",
        );
        return;
      }
      const fields: CareerGapField[] = response.data?.fields ?? [];
      setFormFields(fields);
      const initialValues: Record<string, string | File | null> = {};
      fields.forEach((field) => {
        initialValues[field.field_id] =
          field.type === "file" ? null : String(field.value ?? "");
      });
      setFormData(initialValues);
    } catch (error) {
      console.error("Error fetching Career Gap fields", error);
      Swal.fire(
        "Unable to load",
        "Could not load Career Gap details.",
        "error",
      );
    }
  };

  useEffect(() => {
    if (assignmentTemplateId) void fetchCareerGapForm();
    // Re-fetch when the assigned template changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentTemplateId]);

  const readOnly = disabledStatuses.includes(templateStatus ?? "");
  const isInsuff = templateStatus === "CANDIDATE_INSUFF";

  const handleFieldChange = (fieldId: string, value: string | File | null) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const validateForm = () => {
    const issues: string[] = [];
    formFields.forEach((field) => {
      const value = formData[field.field_id];
      if (field.type === "file") {
        if (
          field.required &&
          !(value instanceof File) &&
          !field.uploaded_files?.length
        ) {
          issues.push(`${field.label} is required`);
        }
        if (value instanceof File) {
          const maxMB = field.file_config?.max_size_mb;
          if (maxMB && value.size > maxMB * 1024 * 1024) {
            issues.push(`${field.label} must not exceed ${maxMB} MB`);
          }
          const types = field.file_config?.allowed_types;
          if (types?.length) {
            const ext = value.name.split(".").pop()?.toLowerCase();
            if (
              !ext ||
              !types
                .map((t) => t.toLowerCase().replace(/^\./, ""))
                .includes(ext)
            ) {
              issues.push(`${field.label}: unsupported file type`);
            }
          }
        }
        return;
      }
      const text = String(value ?? "").trim();
      if (field.required && !text) {
        issues.push(`${field.label} is required`);
        return;
      }
      if (!text) return;
      const min =
        field.validation_rules?.min_length ?? field.validation_rules?.minLength;
      const max =
        field.validation_rules?.max_length ?? field.validation_rules?.maxLength;
      if (min && text.length < min)
        issues.push(`${field.label} must have at least ${min} characters`);
      if (max && text.length > max)
        issues.push(`${field.label} cannot exceed ${max} characters`);
      if (field.validation_rules?.pattern) {
        try {
          if (!new RegExp(field.validation_rules.pattern).test(text)) {
            issues.push(`${field.label} is invalid`);
          }
        } catch {
          console.warn("Invalid API validation pattern for", field.field_id);
        }
      }
      if (
        field.type === "number" &&
        !z.coerce.number().safeParse(text).success
      ) {
        issues.push(`${field.label} must be a number`);
      }
      if (field.type === "dropdown") {
        const allowed = (field.options ?? []).map((o) =>
          typeof o === "string" ? o : o.value,
        );
        if (allowed.length && !allowed.includes(text))
          issues.push(`Select a valid ${field.label}`);
      }
    });
    const start = String(formData.gap_start_date ?? "");
    const end = String(formData.gap_end_date ?? "");
    if (start && end && end < start)
      issues.push("Gap End Date cannot be before Gap Start Date");
    const employmentStart = String(
      formData.previous_employment_start_date ?? "",
    );
    const employmentEnd = String(formData.previous_employment_end_date ?? "");
    if (employmentStart && employmentEnd && employmentEnd < employmentStart) {
      issues.push("Previous Employment End Date cannot be before Start Date");
    }
    return issues[0] ?? null;
  };

  const buildPayload = () => {
    const payload = new FormData();
    const jsonData: Record<string, string | null> = {};
    formFields.forEach((field) => {
      const value = formData[field.field_id];
      if (value instanceof File) payload.append(field.field_id, value);
      else if (field.type !== "file")
        jsonData[field.field_id] = value == null ? "" : String(value);
    });
    payload.append("formData", JSON.stringify(jsonData));
    return payload;
  };

  const saveForm = async (update: boolean) => {
    if (readOnly || !assignmentTemplateId) return;
    const error = validateForm();
    if (error) {
      await Swal.fire("Validation Error", error, "error");
      return;
    }
    try {
      const response = update
        ? await templateUpdateAPI.callApi(buildPayload())
        : await templateSaveAPI.callApi(buildPayload());
      if (response?.success) {
        await Swal.fire(
          "Success",
          response.message || "Career Gap details saved successfully",
          "success",
        );
        dispatch(triggerPackageReload());
        await fetchCareerGapForm();
      } else {
        await Swal.fire(
          "Save Failed",
          response?.message || "Unable to save the form",
          "error",
        );
      }
    } catch (error: any) {
      await Swal.fire(
        "Save Failed",
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
        "error",
      );
    }
  };

  const handleSubmitReply = async () => {
    if (!replyRemarks.trim()) {
      await Swal.fire(
        "Remarks Required",
        "Please enter your remarks.",
        "warning",
      );
      return;
    }
    try {
      const payload = new FormData();
      payload.append(
        "assignment_template_id",
        assignment_template_id ?? assignmentTemplateId ?? "",
      );
      payload.append("assignment_id", assignment_id ?? assignmentId ?? "");
      payload.append("case_id", case_id ?? caseId ?? "");
      payload.append("candidate_id", candidate_id ?? candidateId ?? "");
      payload.append("actor_due_on", actor_type ?? "CANDIDATE");
      payload.append("remarks", replyRemarks.trim());
      replyDocuments.forEach((file) => payload.append("documents", file));
      const response = await insuffResponse.callApi(payload);
      if (response?.success) {
        await Swal.fire(
          "Success",
          "Your response has been submitted successfully.",
          "success",
        );
        setShowReplyModal(false);
        navigate(all_routes.caseSummary);
      } else {
        await Swal.fire(
          "Submission Failed",
          response?.message || "Please try again",
          "error",
        );
      }
    } catch (error: any) {
      await Swal.fire(
        "Submission Failed",
        error?.message || "Unable to submit response",
        "error",
      );
    }
  };

  const sortedFields = [...formFields].sort(
    (a, b) => (a.display_order ?? 999) - (b.display_order ?? 999),
  );
  const textFields = sortedFields.filter((field) => field.type !== "file");
  const fileFields = sortedFields.filter((field) => field.type === "file");

  return (
    <div className="page-wrapper">
      {formFieldsAPI.loading ? (
        <SnipperLoader />
      ) : (
        <div className="content">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card overflow-hidden">
                <div className="border-bottom px-4 py-3">
                  <h5 className="fw-semibold mb-1">Career Gap Comments</h5>
                  <p className="text-muted small mb-0">
                    Employment gap analysis and supporting information
                  </p>
                </div>
                <div className="p-4">
                  {!assignmentTemplateId && (
                    <div className="alert alert-warning">
                      Career Gap Comments template was not found in the assigned
                      package.
                    </div>
                  )}
                  {readOnly && (
                    <div className="alert alert-info">
                      This check is currently{" "}
                      {templateStatus?.replaceAll("_", " ").toLowerCase()}.
                      Details are read-only.
                    </div>
                  )}
                  <div className="row mb-3">
                    {textFields.map((field) => (
                      <div
                        key={field.field_uuid}
                        className={
                          field.type === "textarea" ? "col-12" : "col-md-6"
                        }
                      >
                        <label
                          className="form-label fw-medium"
                          htmlFor={field.field_id}
                        >
                          {field.label}
                          {field.required && (
                            <span className="text-danger ms-1">*</span>
                          )}
                        </label>
                        {field.type === "dropdown" ? (
                          <select
                            id={field.field_id}
                            className="form-select"
                            value={String(formData[field.field_id] ?? "")}
                            disabled={readOnly}
                            required={field.required}
                            onChange={(e) =>
                              handleFieldChange(field.field_id, e.target.value)
                            }
                          >
                            <option value="">
                              {field.placeholder || `Select ${field.label}`}
                            </option>
                            {(field.options ?? []).map((option) => {
                              const value =
                                typeof option === "string"
                                  ? option
                                  : option.value;
                              const label =
                                typeof option === "string"
                                  ? option
                                  : option.label;
                              return (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              );
                            })}
                          </select>
                        ) : field.type === "textarea" ? (
                          <textarea
                            id={field.field_id}
                            className="form-control"
                            rows={4}
                            placeholder={field.placeholder}
                            value={String(formData[field.field_id] ?? "")}
                            disabled={readOnly}
                            required={field.required}
                            onChange={(e) =>
                              handleFieldChange(field.field_id, e.target.value)
                            }
                          />
                        ) : (
                          <input
                            id={field.field_id}
                            type={
                              ["text", "number", "date"].includes(field.type)
                                ? field.type
                                : "text"
                            }
                            className="form-control"
                            placeholder={field.placeholder}
                            value={String(formData[field.field_id] ?? "")}
                            disabled={readOnly}
                            required={field.required}
                            min={field.type === "number" ? 0 : undefined}
                            onChange={(e) =>
                              handleFieldChange(field.field_id, e.target.value)
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {fileFields.length > 0 && (
                    <div className="border rounded-3 p-3 p-md-4 bg-light mt-2">
                      <h5 className="fw-semibold mb-2">
                        Career Gap Supporting Documents
                      </h5>
                      <p className="text-muted small mb-4">
                        Upload documents where applicable. Allowed formats and
                        limits depend on the field.
                      </p>
                      {fileFields.map((field) => {
                        const selected = formData[field.field_id];
                        return (
                          <div
                            key={field.field_uuid}
                            className="row align-items-center mb-4 g-3"
                          >
                            <div className="col-md-7">
                              <label
                                className="form-label fw-medium"
                                htmlFor={field.field_id}
                              >
                                {field.label}
                                {field.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <input
                                id={field.field_id}
                                ref={(el) => {
                                  fileInputRefs.current[field.field_id] = el;
                                }}
                                type="file"
                                className="form-control"
                                accept={field.file_config?.allowed_types
                                  ?.map((t) => `.${t.replace(/^\./, "")}`)
                                  .join(",")}
                                disabled={readOnly}
                                required={
                                  !!field.required &&
                                  !field.uploaded_files?.length
                                }
                                onChange={(e) =>
                                  handleFieldChange(
                                    field.field_id,
                                    e.target.files?.[0] ?? null,
                                  )
                                }
                              />
                              {field.file_config?.max_size_mb && (
                                <small className="text-muted">
                                  Maximum {field.file_config.max_size_mb} MB
                                </small>
                              )}
                              {selected instanceof File && (
                                <div className="small mt-1">
                                  Selected: {selected.name}
                                </div>
                              )}
                            </div>
                            <div className="col-md-5">
                              {(field.uploaded_files ?? []).map(
                                (file, index) => (
                                  <div key={`${file.url}-${index}`}>
                                    <a
                                      href={`${apibaseurl}${file.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <i className="bi bi-file-earmark-text me-2" />
                                      {file.original_name ||
                                        `View uploaded document ${index + 1}`}
                                    </a>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!readOnly && !isInsuff && !!assignmentTemplateId && (
                    <div className="mt-4">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={templateSaveAPI.loading}
                        onClick={() => void saveForm(false)}
                      >
                        {templateSaveAPI.loading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  )}
                  {isInsuff && !!assignmentTemplateId && (
                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={templateUpdateAPI.loading}
                        onClick={() => void saveForm(true)}
                      >
                        {templateUpdateAPI.loading
                          ? "Updating..."
                          : "Save Form & Documents"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => setShowReplyModal(true)}
                      >
                        Reply to Insufficiency
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div
                style={{
                  position: "sticky",
                  top: "60px",
                  alignSelf: "flex-start",
                }}
              >
                <div className="card border-0 shadow-sm">
                  <div
                    className="text-center py-4"
                    style={{ backgroundColor: "#f7f1e3" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                      style={{ width: 70, height: 70 }}
                    >
                      <img src={bulbImage} alt="Instructions" />
                    </div>
                    <h6 className="fw-bold mb-0">Career Gap Instructions</h6>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small">
                      Provide accurate dates for previous employment and the gap
                      period.
                    </p>
                    <p className="text-muted small">
                      Select the category that best describes the reason for the
                      gap, and explain the circumstances clearly.
                    </p>
                    <p className="text-muted small mb-0">
                      Attach supporting documents where available. Your form may
                      be read-only while verification is in progress.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showReplyModal && (
            <div
              className="modal fade show d-block"
              role="dialog"
              aria-modal="true"
              style={{ background: "rgba(0,0,0,.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title fw-bold">
                        Respond to Insufficiency
                      </h5>
                      <small className="text-muted">
                        Provide clarification and supporting documents if
                        applicable.
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowReplyModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <label
                      className="form-label fw-semibold"
                      htmlFor="careerGapReply"
                    >
                      Remarks <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="careerGapReply"
                      className="form-control mb-3"
                      rows={5}
                      value={replyRemarks}
                      onChange={(e) => setReplyRemarks(e.target.value)}
                      placeholder="Enter your response to the verifier"
                    />
                    <label
                      className="form-label fw-semibold"
                      htmlFor="careerGapReplyFiles"
                    >
                      Supporting Documents
                    </label>
                    <input
                      id="careerGapReplyFiles"
                      className="form-control"
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) =>
                        setReplyDocuments(Array.from(e.target.files ?? []))
                      }
                    />
                    {replyDocuments.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="d-flex align-items-center justify-content-between mt-2 small"
                      >
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            setReplyDocuments((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light border"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={insuffResponse.loading}
                      onClick={() => void handleSubmitReply()}
                    >
                      {insuffResponse.loading
                        ? "Submitting..."
                        : "Submit Response"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <Footer />
    </div>
  );
};

export default CareerGap;
